// FormProvider.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Swal from "sweetalert2";
import axios from "axios";
import BASE_URL from "@/Components/auth/Baseurl";
import { axiosInstanceWithToken } from "@/helpers/axios";
import { parsePhoneNumber } from "react-phone-number-input";
import { useAuthHandler } from "@/hooks/AuthHandlers";
import { resizeFile } from "@/helpers/fileResizer";
import { useNavigate } from "react-router-dom";

const formatPhoneForAPI = (phone) => {
  const phoneNumber = parsePhoneNumber(phone, "US");
  return phoneNumber ? phoneNumber.format("E.164") : phone; // E.164 format or original value if invalid
};

// Create the context
const FormContext = createContext();

// Custom hook to use the form context
const useFormContext = () => useContext(FormContext);

const FormProvider = ({ children, validationSchema, defaultValues, type }) => {
  // Initialize react-hook-form with Yup validation schema
  const [formData, setFormData] = useState(defaultValues);
  const { getCurrentUser } = useAuthHandler();
  const [imagesOrder, setImagesOrder] = useState([]);
  console.log("imagesOrder outside", imagesOrder);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    reset,
    watch,
    getValues,
    trigger,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: formData,
  });

  console.log("getValues", getValues());

  const navigate = useNavigate();
  const fieldRefs = useRef({});

  useEffect(() => {
    if (!errors || !fieldRefs.current) return;

    try {
      const errorKeys = Object.keys(errors);
      if (errorKeys.length === 0) return;

      const firstErrorField = errorKeys[0];
      const firstError = errors[firstErrorField];

      if (!firstError) return;

      let errorMessage = "";
      let fieldKey = "";

      // Handle array errors
      if (Array.isArray(firstError)) {
        for (let i = 0; i < firstError.length; i++) {
          const fieldError = firstError[i];
          if (!fieldError || typeof fieldError !== "object") continue;

          const errorFieldKey = Object.keys(fieldError).find(
            (field) => fieldError[field]
          );
          if (!errorFieldKey) continue;

          fieldKey = `${firstErrorField}.${i}.${errorFieldKey}`;
          const fieldRef = fieldRefs.current[fieldKey];

          if (fieldRef?.current) {
            errorMessage = fieldError[errorFieldKey];
            fieldRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            fieldRef.current.focus();
            break;
          }
        }
      }
      // Handle non-array errors
      else if (typeof firstError === "object") {
        fieldKey = firstErrorField;
        const fieldRef = fieldRefs.current[fieldKey];
        if (fieldRef?.current) {
          errorMessage = firstError[Object.keys(firstError)[0]];
          fieldRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          fieldRef.current.focus();
        }
      }

      // Show error message using SweetAlert2 if we found one
      if (errorMessage) {
        Swal.fire({
          icon: "error",
          text: errorMessage,
          confirmButtonColor: "#3085d6",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Error handling form errors:", error);
      Swal.fire({
        icon: "error",
        text: "An unexpected error occurred while processing the form",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "OK",
      });
    }
  }, [errors, fieldRefs]);

  const setMultipleValues = (values) => {
    Object.entries(values).forEach(([key, value]) => {
      setValue(key, value); // Sets each field using setValue
    });
  };

  // Generalized handleChange function
  const handleChange = useCallback(
    (e) => {
      const { name, value, type = "text", checked } = e.target;

      // console.log(name, value, type, checked);
      if (type.toString() === "checkbox") {
        setFormData((prevData) => ({
          ...prevData,
          [name]: checked,
        }));
        setValue(name, parseInt(value) === 1 ? 0 : 1);
      } else if (
        name === "welcome_message_instructions" ||
        name === "rules_instructions"
      ) {
        const newName = name.replace("_instructions", "");
        setFormData((prev) => ({
          ...prev,
          [newName]: getValues(newName) + value,
        }));
        setValue(newName, getValues(newName) + value);
      } else {
        // console.log(name, value)
        setFormData((prev) => ({ ...prev, [name]: value }));
        setValue(name, value);
      }
      trigger(name);
    },
    [getValues, setValue, trigger]
  );

  const appendFormData = useCallback((finalData, data, parentKey = "") => {
    // Skip null, undefined, or empty string
    if (
      data === null ||
      data === undefined ||
      (typeof data === "string" && data.trim() === "")
    ) {
      return;
    }

    if (Array.isArray(data)) {
      // Handle arrays recursively
      data.forEach((item, index) => {
        appendFormData(finalData, item, `${parentKey}[${index}]`);
      });
    } else if (data instanceof Date) {
      // Handle Date objects correctly
      finalData.append(parentKey, data.toISOString());
    } else if (data instanceof File || data instanceof Blob) {
      // Handle File or Blob objects correctly
      finalData.append(parentKey, data);
    } else if (typeof data === "object" && data !== null) {
      // Handle objects recursively
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          if (key === "data_url") continue;
          const value = data[key];
          if (
            value !== null &&
            value !== undefined &&
            !(typeof value === "string" && value.trim() === "")
          ) {
            const newKey = parentKey ? `${parentKey}[${key}]` : key;
            appendFormData(finalData, value, newKey);
          }
        }
      }
    } else {
      // Handle primitives (string, number, boolean)
      finalData.append(parentKey, data);
    }
  }, []);

  // Error handling for validation failures
  function handleValidationError(error) {
    const errorResponse = error?.response?.data;

    if (errorResponse?.message === "Validation failed") {
      const errorMessages = [];
      const errors = errorResponse.errors || {};

      // Collect all unique error messages
      Object.values(errors).forEach((errorArray) => {
        errorArray.forEach((msg) => {
          if (!errorMessages.includes(msg)) {
            errorMessages.push(msg);
          }
        });
      });

      // Format for display (show first 3 unique errors)
      const displayMessage =
        errorMessages.length > 0
          ? errorMessages.slice(0, 3).join("\n") +
            (errorMessages.length > 3
              ? "\n...and " + (errorMessages.length - 3) + " more"
              : "")
          : "Please check your input and try again.";

      Swal.fire({
        icon: "error",
        title: "Validation Error",
        html: `<div style="text-align: left;">
                 <p>${errorResponse.message}:</p>
                 <ul>
                   ${errorMessages
                     .slice(0, 5)
                     .map((msg) => `<li>${msg}</li>`)
                     .join("")}
                   ${
                     errorMessages.length > 5
                       ? `<li>...and ${
                           errorMessages.length - 5
                         } more issues</li>`
                       : ""
                   }
                 </ul>
               </div>`,
        confirmButtonText: "Fix Issues",
      });
    } else {
      console.log(error);
      // Fallback for other errors
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorResponse?.message || "An unknown error occurred",
      });
    }
  }

  // Handle form submission
  const onSubmit = useCallback(
    async (data, event) => {
      try {
        event.preventDefault();
        console.log(errors, data);

        // Create a new FormData object
        const finalData = new FormData();

        // console.log(data)

        let updatedImages = data?.images.map((image) => {
          const { data_url, ...rest } = image; // Destructure to exclude `data_url`
          return rest;
        });

        data?.airports?.forEach((airport) => {
          airport.surface = airport?.surface?.join(", ");
          airport.fuel = airport?.fuel?.join(", ");
          airport.ctaf_unicom = parseFloat(airport?.ctaf_unicom);
        });

        updatedImages.forEach((image, index) => {
          if (image?.file instanceof File) {
            if (image.file) {
              finalData.append(`images[${index}][file]`, image.file);
            }
            finalData.append(
              `images[${index}][description]`,
              image.description || "No Name"
            );
          }
        });

        if (data?.deleted_images) {
          data.deleted_images = data.deleted_images.join(",");
        }

        const updatedData = {
          ...data,
          is_draft: 0,
          host_id: JSON.parse(localStorage.getItem("data"))?.id,
          // instant_booking: getValues('instant_booking') === true ? 1 : 0,
        };

        if (+getValues("custom_period_pricing") === 0) {
          delete updatedData.custom_periods;
        }
        console.log(getValues("extra_services"));
        if (+getValues("extra_services") === 0) {
          delete updatedData.extra_service;
        }
        appendFormData(finalData, updatedData);

        // -----------------------------------------------------------------------------------------------------------

        // If data?.id exists, append _method: "PUT" for update
        if (data?.id && !data?.draft_id) {
          finalData.append("_method", "PUT");
        }

        const imageOrder = updatedImages?.map((item, index) => {
          return {
            file: typeof item?.image === "string" ? item?.image : item?.file,
            order: index,
          };
        });
        setImagesOrder(imageOrder?.length > 0 ? imageOrder : []);

        try {
          const apiRes = await axios.post(
            data?.id && !data?.draft_id
              ? `${BASE_URL}/listing/${data?.id}`
              : `${BASE_URL}/listing`,
            finalData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );

          const savedImages = apiRes?.data?.listing_images;
          console.log({ savedImages, imageOrder });
          const updatedOrderedImages = imageOrder?.map((item) => {
            if (
              typeof item?.file === "string" &&
              item?.file?.includes("https://s3.")
            ) {
              return {
                ...item,
                file: item?.file,
              };
            } else {
              const findImage = savedImages?.find((myEl) =>
                myEl?.image?.includes(item?.file?.name)
              );
              console.log({ findImage });
              return {
                ...item,
                file: findImage?.image,
                order: findImage?.order,
              };
            }
          });

          console.log({ updatedOrderedImages });
          if (data?.id) {
            await axios.post(
              `${BASE_URL}/sort-images`,
              {
                images: updatedOrderedImages
                  ?.map((item) => item?.file)
                  ?.filter((file) => file !== null && file !== undefined),
              },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              }
            );
          }

          Swal.fire({
            position: "center",
            html: `
                      <div style="display: flex; flex-direction: column; align-items: center;">
                            <svg
                                viewBox="0 0 1024 1024"
                                height="4em"
                                width="4em"
                                style="margin-bottom: 10px;"
                            >
                                <!-- Outer circle with color #8ec639 -->
                                <path fill="#8ec639" d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z" />
                                
                                <!-- Check mark with color #A2c66b -->
                                <path fill="#A2c66b" d="M699 353h-46.9c-10.2 0-19.9 4.9-25.9 13.3L469 584.3l-71.2-98.8c-6-8.3-15.6-13.3-25.9-13.3H325c-6.5 0-10.3 7.4-6.5 12.7l124.6 172.8a31.8 31.8 0 0051.7 0l210.6-292c3.9-5.3.1-12.7-6.4-12.7z" />
                            </svg>
                          <p style="margin: 0; font-size: 1.2em;"> Listing Created Successfully.</p>
                      </div>
                  `,
            title:
              "Your changes have been submitted to Admin. Please stand by for approval.",
            showConfirmButton: true,
          });

          navigate("/my_listing");
        } catch (error) {
          handleValidationError(error);
        }

        // ------------------------------------------------------------------------------------------------------------

        setFormData(updatedData);
        // console.log('Submitted Data:', updatedData);
      } catch (err) {
        console.log(err);
      }
    },
    [appendFormData, getValues, navigate]
  );

  const onProfileSubmit = useCallback(
    async (data, event) => {
      try {
        event.preventDefault();

        console.log(errors, data);

        if (errors && Object.keys(errors).length > 0) {
          return;
        }
        // Create a new FormData object and append only the necessary fields
        const finalData = new FormData();

        // Helper function to append data if it's not null or empty
        const appendIfNotEmpty = (key, value) => {
          if (value !== null && value !== "" && value !== "null") {
            finalData.append(key, value);
          }
        };

        // Format phone numbers
        const formattedPhone = formatPhoneForAPI(data.phone);
        const formattedOtherPhone = formatPhoneForAPI(data.other_phone);

        appendIfNotEmpty("first_name", data.first_name);
        appendIfNotEmpty("last_name", data.last_name);
        appendIfNotEmpty("username", data.username);
        appendIfNotEmpty("display_name", data.display_name);
        appendIfNotEmpty("email", data.email);
        appendIfNotEmpty("phone", formattedPhone);
        appendIfNotEmpty("other_phone", formattedOtherPhone);
        appendIfNotEmpty("native_language", data.native_language);
        appendIfNotEmpty("other_language", data.other_language);
        appendIfNotEmpty("additional_email", data.additional_email);
        appendIfNotEmpty("bio", data.bio);
        appendIfNotEmpty("address", data.address);
        appendIfNotEmpty("unit_no", data.unit_no);
        appendIfNotEmpty("city", data.city);
        appendIfNotEmpty("state", data.state);
        appendIfNotEmpty("zip_code", data.zip_code);
        appendIfNotEmpty("country", data.country);
        appendIfNotEmpty("neighbourhood", data.neighbourhood);
        appendIfNotEmpty("contact_name", data.contact_name);
        appendIfNotEmpty("contact_relationship", data.contact_relationship);
        appendIfNotEmpty("contact_email", data.contact_email);
        appendIfNotEmpty(
          "contact_phone",
          formatPhoneForAPI(data.contact_phone)
        );
        // appendIfNotEmpty("facebook_url", data.facebook_url || "");
        // appendIfNotEmpty("instagram_url", data.instagram_url || "");
        // appendIfNotEmpty("youtube_url", data.youtube_url || "");
        // appendIfNotEmpty("twitter_url", data.twitter_url || "");
        // appendIfNotEmpty("linkedin_url", data.linkedin_url || "");
        // appendIfNotEmpty("pinterest_url", data.pinterest_url || "");
        // appendIfNotEmpty("vimeo_url", data.vimeo_url || "");
        // appendIfNotEmpty("airbnb_url", data.airbnb_url || "");
        // appendIfNotEmpty("top_advisor_url", data.top_advisor_url || "");
        appendIfNotEmpty("_method", "PUT");

        if (typeof data?.image !== "string" && data?.image) {
          finalData.append("image", await resizeFile(data.image[0]));
        }

        if (data?.air_men instanceof File) {
          finalData.append("air_men", await resizeFile(data.air_men));
        } else if (
          data?.air_men instanceof FileList &&
          data?.air_men.length > 0
        ) {
          finalData.append("air_men", await resizeFile(data.air_men[0]));
        }

        if (data?.air_men_back instanceof File) {
          finalData.append("air_men_back", await resizeFile(data.air_men_back));
        } else if (
          data?.air_men_back instanceof FileList &&
          data?.air_men_back.length > 0
        ) {
          finalData.append(
            "air_men_back",
            await resizeFile(data.air_men_back[0])
          );
        }

        if (data?.driving_license instanceof File) {
          finalData.append(
            "driving_license",
            await resizeFile(data.driving_license)
          );
        } else if (
          data?.driving_license instanceof FileList &&
          data?.driving_license.length > 0
        ) {
          finalData.append(
            "driving_license",
            await resizeFile(data.driving_license[0])
          );
        }

        if (data?.driving_license_back instanceof File) {
          finalData.append(
            "driving_license_back",
            await resizeFile(data.driving_license_back)
          );
        } else if (
          data?.driving_license_back instanceof FileList &&
          data?.driving_license_back.length > 0
        ) {
          finalData.append(
            "driving_license_back",
            await resizeFile(data.driving_license_back[0])
          );
        }

        await axiosInstanceWithToken
          .post(`/profile`, finalData)
          .then(async (res) => {
            await getCurrentUser().then((response) => {
              localStorage.setItem(
                "data",
                JSON.stringify(response?.data?.user)
              );
            });
          });

        Swal.fire({
          position: "center",
          html: `
                      <div style="display: flex; flex-direction: column; align-items: center;">
                            <svg
                                viewBox="0 0 1024 1024"
                                height="4em"
                                width="4em"
                                style="margin-bottom: 10px;"
                            >
                                <!-- Outer circle with color #8ec639 -->
                                <path fill="#8ec639" d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z" />
                                
                                <!-- Check mark with color #A2c66b -->
                                <path fill="#A2c66b" d="M699 353h-46.9c-10.2 0-19.9 4.9-25.9 13.3L469 584.3l-71.2-98.8c-6-8.3-15.6-13.3-25.9-13.3H325c-6.5 0-10.3 7.4-6.5 12.7l124.6 172.8a31.8 31.8 0 0051.7 0l210.6-292c3.9-5.3.1-12.7-6.4-12.7z" />
                            </svg>
                          <p style="margin: 0; font-size: 1.2em;"> Profile Updated Successfully.</p>
                      </div>
                  `,
          showConfirmButton: true,
        });
      } catch (err) {
        handleValidationError(err);
      }
    },
    [errors, getCurrentUser]
  );

  // Handle draft saving (e.g., saving the form data to localStorage)
  const handleDraft = useCallback(async () => {
    const data = {
      ...getValues(),
      host_id: JSON.parse(localStorage.getItem("data"))?.id,
      is_draft: 1,
      instant_booking: getValues("instant_booking") === true ? 1 : 0,
    };
    if (getValues("custom_period_pricing") === 0) {
      delete data.custom_periods;
    }
    if (getValues("extra_services") === 0) {
      delete data.extra_service;
    }

    data?.airports?.forEach((airport) => {
      // airport.surface = airport?.surface ? airport?.surface?.join(", ") : '';
      // airport.fuel = airport?.fuel ? airport?.fuel?.join(", ") : '';
      airport.ctaf_unicom = parseFloat(airport?.ctaf_unicom);
    });

    const finalData = new FormData();
    // Populate the FormData object with the key-value pairs from data
    // for (const key in data) {
    //     if (data.hasOwnProperty(key)) {
    //         finalData.append(key, data[key]);
    //     }
    // }

    appendFormData(finalData, data);

    // console.log("Original Data", data);

    // console.log(finalData);

    try {
      await axios.post(`${BASE_URL}/listing`, finalData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      Swal.fire({
        position: "center",
        html: `
                  <div style="display: flex; flex-direction: column; align-items: center;">
                        <svg
                            viewBox="0 0 1024 1024"
                            height="4em"
                            width="4em"
                            style="margin-bottom: 10px;"
                        >
                            <!-- Outer circle with color #8ec639 -->
                            <path fill="#8ec639" d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z" />
                            
                            <!-- Check mark with color #A2c66b -->
                            <path fill="#A2c66b" d="M699 353h-46.9c-10.2 0-19.9 4.9-25.9 13.3L469 584.3l-71.2-98.8c-6-8.3-15.6-13.3-25.9-13.3H325c-6.5 0-10.3 7.4-6.5 12.7l124.6 172.8a31.8 31.8 0 0051.7 0l210.6-292c3.9-5.3.1-12.7-6.4-12.7z" />
                        </svg>
                      <p style="margin: 0; font-size: 1.2em;">You Draft has been submitted!.</p>
                  </div>
              `,
        title: "Your has been saved.",
        showConfirmButton: true,
        timer: 1500,
      });

      navigate("/my_listing");
    } catch (error) {
      handleValidationError(error);
    }

    // localStorage.setItem('formDraft', JSON.stringify(data));
    // console.log('Draft saved:', data);
  }, [appendFormData, getValues, navigate]);

  // Context value to provide to children
  const contextValue = {
    register,
    handleSubmit: handleSubmit(onSubmit),
    handleChange,
    onProfileSubmit,
    handleDraft,
    setValue,
    errors,
    formData,
    reset,
    watch,
    trigger,
    getValues,
    setMultipleValues,
    fieldRefs,
    isSubmitting,
  };

  return (
    <FormContext.Provider value={contextValue}>{children}</FormContext.Provider>
  );
};

export { FormProvider, useFormContext };
